import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  useOrderSchedule,
  useDeleteOrderSchedule,
  usePauseOrderSchedule,
  useResumeOrderSchedule,
  useDeleteOrderTemplate,
  useTriggerOrderSchedule,
} from '../../hooks/useApi';
import type { OrderScheduleStatus, RecurrenceType, ScheduledCargo, OrderTemplate } from '../../types';

const statusConfig: Record<OrderScheduleStatus, { label: string; color: 'default' | 'success' | 'warning' }> = {
  ACTIVE: { label: 'Aktiv', color: 'success' },
  PAUSED: { label: 'Pauseret', color: 'warning' },
  COMPLETED: { label: 'Afsluttet', color: 'default' },
};

const cargoStatusConfig: Record<string, { label: string; color: 'default' | 'success' | 'info' | 'error' | 'warning' }> = {
  PENDING: { label: 'Afventer', color: 'default' },
  CREATED: { label: 'Oprettet', color: 'success' },
  UPDATED: { label: 'Opdateret', color: 'info' },
  FAILED: { label: 'Fejlet', color: 'error' },
};

const recurrenceLabels: Record<RecurrenceType, string> = {
  DAILY: 'Dagligt',
  WEEKLY: 'Ugentligt',
  BIWEEKLY: 'Hver 2. uge',
  EVERY_X_DAYS: 'Hver X dage',
  SPECIFIC_WEEKDAYS: 'Bestemte ugedage',
};

const weekdayNames = ['', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'];

function fmtDate(d: string | null) {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(d: string | null) {
  if (!d) return '\u2014';
  return new Date(d).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function getRouteSummary(template: OrderTemplate): string {
  const allStops = [...(template.pickupTemplate || []), ...(template.dropoffTemplate || [])];
  const cities = allStops.map(s => s.place?.city || s.place?.postcode).filter(Boolean);
  if (cities.length === 0) return '\u2014';
  return cities.join(' \u2192 ');
}

function TemplateCard({ template, scheduleId, onDelete }: { template: OrderTemplate; scheduleId: string; onDelete: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const cargos = template.generatedCargos || [];

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: expanded ? 0 : undefined }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {template.name || template.customerName || 'Ordreskabelon'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {template.customerName ? `${template.customerName} \u2022 ` : ''}{getRouteSummary(template)}
              {template.cargoType ? ` \u2022 ${template.cargoType}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={() => navigate(`/ordreskemaer/${scheduleId}/ordrer/${template.id}/rediger`)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(template.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Genererede ordrer ({cargos.length})
          </Typography>
          {cargos.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Ingen ordrer endnu</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dato</TableCell>
                    <TableCell>DORA ref.</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Oprettet i DORA</TableCell>
                    <TableCell>Fejl</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cargos.map((cargo: ScheduledCargo) => {
                    const cs = cargoStatusConfig[cargo.status] || { label: cargo.status, color: 'default' as const };
                    return (
                      <TableRow key={cargo.id}>
                        <TableCell>{fmtDate(cargo.scheduledDate)}</TableCell>
                        <TableCell>{cargo.doraCargoRef || '\u2014'}</TableCell>
                        <TableCell><Chip label={cs.label} color={cs.color} size="small" /></TableCell>
                        <TableCell>{fmtDateTime(cargo.createdInDoraAt)}</TableCell>
                        <TableCell sx={{ color: 'error.main', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {cargo.errorMessage || ''}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
}

export default function OrderScheduleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: schedule, isLoading, error } = useOrderSchedule(id || '');
  const deleteSchedule = useDeleteOrderSchedule();
  const pauseSchedule = usePauseOrderSchedule();
  const resumeSchedule = useResumeOrderSchedule();
  const deleteTemplate = useDeleteOrderTemplate();
  const triggerSchedule = useTriggerOrderSchedule();

  const [confirmDialog, setConfirmDialog] = useState<'delete' | 'pause' | 'resume' | null>(null);
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  if (isLoading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (error || !schedule) return <Alert severity="error">Ordreskema ikke fundet</Alert>;

  const s = schedule;
  const status = s.status as OrderScheduleStatus;
  const sc = statusConfig[status] || { label: status, color: 'default' as const };
  const templates = s.orderTemplates || [];

  function recurrenceText(): string {
    let text: string;
    if (s.recurrenceType === 'EVERY_X_DAYS') {
      text = `Hver ${s.intervalDays}. dag`;
    } else if (s.recurrenceType === 'SPECIFIC_WEEKDAYS') {
      const days = (s.weekdays || []).map(d => weekdayNames[d] || d).join(', ');
      return days || 'Bestemte ugedage';
    } else {
      text = recurrenceLabels[s.recurrenceType as RecurrenceType] || s.recurrenceType;
    }
    const days = (s.weekdays || []);
    if (days.length > 0) {
      text += ` (${days.map(d => weekdayNames[d] || d).join(', ')})`;
    }
    return text;
  }

  async function handleDelete() {
    try {
      await deleteSchedule.mutateAsync(s.id);
      setSnackbar({ open: true, message: 'Ordreskema slettet', severity: 'success' });
      navigate('/ordreskemaer');
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke slette ordreskema', severity: 'error' });
    }
    setConfirmDialog(null);
  }

  async function handlePause() {
    try {
      await pauseSchedule.mutateAsync(s.id);
      setSnackbar({ open: true, message: 'Ordreskema sat på pause', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke sætte på pause', severity: 'error' });
    }
    setConfirmDialog(null);
  }

  async function handleResume() {
    try {
      await resumeSchedule.mutateAsync(s.id);
      setSnackbar({ open: true, message: 'Ordreskema genoptaget', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke genoptage', severity: 'error' });
    }
    setConfirmDialog(null);
  }

  async function handleTrigger() {
    try {
      const result = await triggerSchedule.mutateAsync(s.id);
      if (result.created > 0 || result.failed > 0) {
        const msg = `Oprettet: ${result.created}, Fejlet: ${result.failed}${result.errors.length ? ` — ${result.errors[0]}` : ''}`;
        setSnackbar({ open: true, message: msg, severity: result.failed > 0 ? 'error' : 'success' });
      } else {
        setSnackbar({ open: true, message: 'Ingen ordrer at oprette (alle er allerede oprettet)', severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke køre skema', severity: 'error' });
    }
  }

  async function handleDeleteTemplate() {
    if (!deleteTemplateId) return;
    try {
      await deleteTemplate.mutateAsync({ scheduleId: s.id, templateId: deleteTemplateId });
      setSnackbar({ open: true, message: 'Ordreskabelon slettet', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke slette skabelon', severity: 'error' });
    }
    setDeleteTemplateId(null);
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/ordreskemaer')}>Tilbage</Button>
        <Typography variant="h5" fontWeight={600} sx={{ flex: 1 }}>{s.name}</Typography>
        <Chip label={sc.label} color={sc.color} />
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/ordreskemaer/${s.id}/rediger`)}>
          Rediger
        </Button>
        {status === 'ACTIVE' && (
          <Button variant="outlined" color="warning" startIcon={<PauseIcon />} onClick={() => setConfirmDialog('pause')}>
            Pause
          </Button>
        )}
        {status === 'PAUSED' && (
          <Button variant="outlined" color="success" startIcon={<PlayArrowIcon />} onClick={() => setConfirmDialog('resume')}>
            Genoptag
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<RocketLaunchIcon />}
          onClick={handleTrigger}
          disabled={triggerSchedule.isPending}
        >
          {triggerSchedule.isPending ? 'Kører...' : 'Kør nu'}
        </Button>
        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDialog('delete')}>
          Slet
        </Button>
      </Box>

      {/* Schedule info card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Skema info</Typography>
          <Table size="small">
            <TableBody>
              <TableRow><TableCell sx={{ fontWeight: 500 }}>Gentagelse</TableCell><TableCell>{recurrenceText()}</TableCell></TableRow>
              <TableRow><TableCell sx={{ fontWeight: 500 }}>Periode</TableCell><TableCell>{fmtDate(s.startDate)} \u2013 {fmtDate(s.endDate)}</TableCell></TableRow>
              <TableRow><TableCell sx={{ fontWeight: 500 }}>Opret ordre</TableCell><TableCell>{s.leadTimeDays} dag(e) før</TableCell></TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Order templates section */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h6">Ordreskabeloner ({templates.length})</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => navigate(`/ordreskemaer/${s.id}/ordrer/ny`)}>
          Tilføj ordre
        </Button>
      </Box>

      {templates.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center">
              Ingen ordreskabeloner endnu. Tilføj en for at komme i gang.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        templates.map((template: OrderTemplate) => (
          <TemplateCard
            key={template.id}
            template={template}
            scheduleId={s.id}
            onDelete={(templateId) => setDeleteTemplateId(templateId)}
          />
        ))
      )}

      {/* Confirm dialogs */}
      <Dialog open={confirmDialog === 'delete'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Slet ordreskema</DialogTitle>
        <DialogContent><DialogContentText>Er du sikker på du vil slette "{s.name}"? Allerede oprettede ordrer i DORA påvirkes ikke.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
          <Button color="error" onClick={handleDelete} disabled={deleteSchedule.isPending}>Slet</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog === 'pause'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Pause ordreskema</DialogTitle>
        <DialogContent><DialogContentText>Sæt "{s.name}" på pause? Der oprettes ingen nye ordrer, indtil det genoptages.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
          <Button color="warning" onClick={handlePause} disabled={pauseSchedule.isPending}>Pause</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog === 'resume'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Genoptag ordreskema</DialogTitle>
        <DialogContent><DialogContentText>Genoptag "{s.name}"? Ordrer vil igen blive oprettet automatisk.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
          <Button color="success" onClick={handleResume} disabled={resumeSchedule.isPending}>Genoptag</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTemplateId} onClose={() => setDeleteTemplateId(null)}>
        <DialogTitle>Slet ordreskabelon</DialogTitle>
        <DialogContent><DialogContentText>Er du sikker på du vil slette denne ordreskabelon?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTemplateId(null)}>Annuller</Button>
          <Button color="error" onClick={handleDeleteTemplate} disabled={deleteTemplate.isPending}>Slet</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
